import React from 'react';
import styled from 'styled-components';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { ICalculatorParticipant, ICalculatorRule, ICalculatorRuleRemainder, ICalculatorRuleSharePolicyShare, ShareRemainderType } from 'types';
import { toTitleCase } from 'utils';
import { renderRemainderBeneficiaryDescription, renderShareRuleDescription, Semibold } from '../TipShareRules/TipShareRules';

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const DetailsContainer = styled(Column)`
  padding: ${toRem(theme.space.lg)};
  border-radius: ${toRem(theme.borderRadius.small)};
  background-color: ${theme.color.neutral[50]};
  gap: ${toRem(theme.space.md)};

  ul {
    margin-left: ${toRem(theme.space.lg)};
  }
`;

const DetailsText = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

const mapBenefitingJobs = (shares: ICalculatorRuleSharePolicyShare[]) => {
  if (!shares?.length)
    return (
      <DetailsText size="xsmall" fontWeight="semibold">
        None
      </DetailsText>
    );

  return (
    <ul>
      {shares.map((shareItem, i) => (
        <DetailsText size="xsmall" key={`share-${i}`}>
          <li>{renderShareRuleDescription(shareItem)}</li>
        </DetailsText>
      ))}
    </ul>
  );
};

const mapRemainderJobs = (remainder: ICalculatorRuleRemainder) => (
  <>
    <DetailsText size="xsmall" fontWeight="semibold">
      {remainder.type === ShareRemainderType.SELF ? 'Self' : 'Pooled'}
    </DetailsText>
    {remainder.type === ShareRemainderType.POOL && (
      <ul>
        {remainder.beneficiaries.map((beneficiary, i) => (
          <DetailsText size="xsmall" key={`remainder-${i}`}>
            <li>{renderRemainderBeneficiaryDescription(beneficiary)}</li>
          </DetailsText>
        ))}
      </ul>
    )}
  </>
);

const mapDefaultPoolJobs = (jobs: ICalculatorParticipant[]) => (
  <ul>
    {jobs.map((job, i) => (
      <DetailsText size="xsmall" key={`job-${i}`}>
        <li>
          <Semibold>{toTitleCase(job?.name)}</Semibold>
        </li>
      </DetailsText>
    ))}
  </ul>
);

interface ITipShareBreakdownProps {
  tipShare: ICalculatorRule;
  className?: string;
}

const TipShareBreakdown = ({ tipShare, className }: ITipShareBreakdownProps) => (
  <DetailsContainer data-cy="rules-details" className={className}>
    {tipShare.default ? (
      <>
        {tipShare.includeNoLabor && (
          <Text size="xxsmall" fontWeight="medium">
            Tips included without timecards
          </Text>
        )}
        {tipShare.contributor && (
          <Column>
            <Text size="xxsmall" fontWeight="medium">
              Contributing Jobs:
            </Text>
            {mapDefaultPoolJobs([tipShare.contributor, ...tipShare.additionalContributors])}
          </Column>
        )}
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Receiving Jobs:
          </Text>
          {tipShare.sharePolicy?.remainder && mapDefaultPoolJobs(tipShare.sharePolicy.remainder.beneficiaries.map((beneficiary) => beneficiary.participants[0]))}
        </Column>
      </>
    ) : (
      <>
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Benefits:
          </Text>
          {mapBenefitingJobs(tipShare.sharePolicy?.shares)}
        </Column>
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Remainder:
          </Text>
          {tipShare.sharePolicy?.remainder && mapRemainderJobs(tipShare.sharePolicy.remainder)}
        </Column>
      </>
    )}
  </DetailsContainer>
);

export default TipShareBreakdown;
